import API from '@/APIs/API';
import {TextBox} from '@/Core/UI';
import RootStore from '@/Core/mobx/RootStore';
import {Box, Button, CircularProgress, Divider, Grid, InputAdornment, TextField, Typography} from '@material-ui/core';
import {
    ChevronLeft,
    ChevronRight,
    Refresh,
    Search,
    ThumbDown,
    ThumbDownOutlined,
    ThumbUp,
    ThumbUpOutlined,
} from '@material-ui/icons';
import Markdown from 'markdown-to-jsx';
import {observer} from 'mobx-react-lite';
import {useSession} from 'next-auth/react';
import React, {useEffect, useRef, useState} from 'react';
import {useMutation} from 'react-query';
import {useStyles} from './Panel.styles';

interface PanelProps {
    title?: string;
    subtitle?: string;
    className?: string;
    onValueChangedNotes?: (value: string) => void;
    isLiveCall?: boolean;
}

interface FeedbackPanelProps {
    item: {
        responseId: string;
        wasHelpful: boolean | null;
    };
    isInitialResponse?: boolean;
}

const AgentAssistPanel: React.FC<PanelProps> = observer(({className, onValueChangedNotes, isLiveCall = false}) => {
    const session = useSession();

    // Determine if we want to allow this
    const shouldAllowResume = true;
    const classes = useStyles(shouldAllowResume);
    const markdownOverrides = {
        hr: {
            props: {
                className: classes.markdownFormatting,
            },
        },
        h3: {
            props: {
                className: classes.markdownFormattingExtendedH3,
            },
        },
        ul: {
            props: {
                className: classes.markdownFormattingExtended,
            },
        },
        ol: {
            props: {
                className: classes.markdownFormattingExtended,
            },
        },
        li: {
            props: {
                className: classes.markdownFormattingExtended,
            },
        },
        p: {
            props: {
                className: classes.markdownFormattingExtended,
            },
        },
    };
    const {assistStore, agentPortalStore} = RootStore;
    const {parameterStore} = agentPortalStore;
    const {
        sendQuery,
        isSendingQuery,
        queryHistory,
        sessionId,
        isCreatingSession,
        initialResponse,
        startAssist,
        resetSession,
        notes,
    } = assistStore;
    const [queryText, setQueryText] = useState<string>('');
    const chatHistoryRef = useRef<HTMLDivElement>(null);
    const lastQueryRef = useRef<HTMLDivElement>(null);

    const logStartAiAssistMutation = useMutation({
        mutationFn: (agent: string) => API.LogToMixPanel('agent-start-ai-assist-click', {agent}),
    });

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQueryText(event.target.value);
    };

    const handleSendQuery = async () => {
        await sendQuery(queryText);
        setQueryText('');
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && queryText.trim() && !isSendingQuery) {
            handleSendQuery();
        }
    };

    const assistChatHistoryActive = sessionId && queryHistory?.length > 0;

    // Auto-scroll to bottom when query history updates
    useEffect(() => {
        if (lastQueryRef.current) {
            lastQueryRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        }
    }, [queryHistory?.length]);

    return (
        <Box className={`${classes.contentCard} ${className || ''}`}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <Typography className={classes.sectionTitle}>Case Notes</Typography>
                </div>
            </div>
            <TextBox
                fullWidth
                id={'notes'}
                label="Notes"
                value={parameterStore.approval.notes.value || notes}
                multiline
                rows={15}
                onChange={(value) => onValueChangedNotes?.(value)}
                variant="outlined"
            />
            <Divider style={{width: '75%', margin: '24px 24px', height: '1px'}} />
            {shouldAllowResume && (
                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '8px'}}>
                    <Button
                        variant="text"
                        color="secondary"
                        className={classes.resetButton}
                        onClick={() => resetSession(false, isLiveCall)}
                        disabled={!sessionId}
                    >
                        <Refresh />
                    </Button>
                </div>
            )}
            <TextField
                disabled={!sessionId}
                fullWidth
                variant="outlined"
                className={classes.queryInput}
                placeholder="Ask Your Copilot"
                value={queryText}
                onChange={handleQueryChange}
                onKeyDown={handleKeyPress}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Search />
                        </InputAdornment>
                    ),
                }}
            />
            {sessionId && (
                <Button
                    variant="contained"
                    color="secondary"
                    className={classes.helpMeButton}
                    disabled={!queryText.trim()}
                    onClick={handleSendQuery}
                >
                    {isSendingQuery ? <CircularProgress size={20} style={{color: '#456D69'}} /> : 'HELP ME'}
                </Button>
            )}
            {assistChatHistoryActive && (
                <div ref={chatHistoryRef} id="assist-chat-history" className={classes.chatContainer}>
                    {queryHistory.map((item, index) => (
                        <div key={item.responseId} ref={index === queryHistory.length - 1 ? lastQueryRef : null}>
                            <Grid container alignItems="flex-start" justify="flex-end">
                                <Grid item xs={11}>
                                    <div className={classes.userMessageBubble}>
                                        <Typography variant="body2" className={classes.chatHistory}>
                                            <Markdown options={{overrides: markdownOverrides}}>{item.query}</Markdown>
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={1} style={{display: 'flex', justifyContent: 'flex-end'}}>
                                    <ChevronLeft />
                                </Grid>
                            </Grid>
                            <Grid container alignItems="flex-start">
                                <Grid item xs={1} style={{display: 'flex', justifyContent: 'flex-start'}}>
                                    <ChevronRight />
                                </Grid>
                                <Grid item xs={11}>
                                    <div className={classes.aiMessageBubble}>
                                        <Typography variant="body2" className={classes.chatHistory}>
                                            <Markdown options={{overrides: markdownOverrides}}>
                                                {item.response}
                                            </Markdown>
                                        </Typography>
                                        <FeedbackPanel item={item} />
                                    </div>
                                </Grid>
                            </Grid>
                        </div>
                    ))}
                </div>
            )}
            {(isLiveCall || (!isLiveCall && !sessionId)) && (
                <Typography variant="h6" className={classes.sectionTitle}>
                    AI Assist
                </Typography>
            )}
            {initialResponse.response ? (
                isLiveCall && (
                    <div className={classes.prePromptContainer}>
                        <Typography variant="body2" className={classes.initialResponse}>
                            <Markdown options={{overrides: markdownOverrides}}>{initialResponse.response}</Markdown>
                        </Typography>
                    </div>
                )
            ) : isCreatingSession ? (
                <div style={{display: 'flex', justifyContent: 'center', padding: '20px'}}>
                    <CircularProgress style={{color: '#456D69'}} />
                </div>
            ) : (
                <Typography variant="body2" className={classes.sectionDescriptionInactive}>
                    <span style={{fontWeight: 'bold'}}>Disclaimer:</span>
                    <br />
                    <span style={{fontWeight: 'bold', fontSize: 12}}>
                        This tool provides information for internal purposes only and has not been reviewed for
                        accuracy. Please use your judgment and verify the information before relying on it to advise a
                        client.
                    </span>
                    <span style={{fontWeight: 'bold', fontSize: 12}}>
                        {' '}
                        Do not enter or share any sensitive or personally identifiable information, including names,
                        emails, phone numbers, Social Security numbers, driver's license numbers, banking details,
                        health or prescription details, or any other sensitive information or data that may be used to
                        identify the client. Failure to safeguard our clients' personal or sensitive data may result in
                        disciplinary action, up to and including termination of employment. By clicking "Start AI
                        Assist" below I agree to the above and will comply.
                    </span>
                </Typography>
            )}
            {!(sessionId || isCreatingSession) && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        if (session.data?.user?.email) {
                            logStartAiAssistMutation.mutate(session.data.user.email);
                        }
                        startAssist({shouldAllowResume, isLiveCall});
                    }}
                    disabled={!!sessionId}
                    className={classes.assistStartButton}
                >
                    START AI ASSIST
                </Button>
            )}
            {initialResponse.responseId && isLiveCall && (
                <div className={classes.initialResponseFeedbackContainer}>
                    <FeedbackPanel item={initialResponse} isInitialResponse={true} />
                </div>
            )}
        </Box>
    );
});

const FeedbackPanel: React.FC<FeedbackPanelProps> = observer(({item, isInitialResponse = false}) => {
    const classes = useStyles();
    const {assistStore} = RootStore;
    const {handleFeedbackChange} = assistStore;

    return (
        <div className={classes.feedbackContainer} style={isInitialResponse ? {position: 'relative'} : {}}>
            {item.wasHelpful === true ? (
                <ThumbUp className={classes.thumbsIcon} onClick={() => handleFeedbackChange(item.responseId, true)} />
            ) : (
                <ThumbUpOutlined
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, true)}
                />
            )}
            {item.wasHelpful === false ? (
                <ThumbDown
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, false)}
                />
            ) : (
                <ThumbDownOutlined
                    className={classes.thumbsIcon}
                    onClick={() => handleFeedbackChange(item.responseId, false)}
                />
            )}
        </div>
    );
});

export default AgentAssistPanel;
